import type { ParsedUrlQuery } from "querystring"

import type { NextRouter } from "next/router"
import { useRouter } from "next/router"

import type { Country } from "@bounce/i18n"
import { DEFAULT_LANGUAGE, useLocale } from "@bounce/i18n"
import type { RequireAtLeastOne } from "@bounce/util"

import { getCanonicalUrl } from "./urls"
import type { GetStoreLocationQuery } from "../bounce/bounceSdk.types.gen"
import type { CityData } from "../bounce/cities"

type SlugData = {
  slug: string
  directorySlug: string
}

type GetCityUrlPathArgs = {
  city: SlugData
  directory?: "city" | "luggage-storage" | "ads"
}

type GetPoiUrlPathArgs = {
  city: SlugData
  poi: SlugData
  directory?: "city" | "luggage-storage" | "ads"
}

type GetStoreUrlPathArgs = {
  store: Pick<GetStoreLocationQuery["store"], "slug"> & {
    city: Pick<CityData, "slug">
  }
}

export const DEFAULT_COUNTRY = "US"
const DEFAULT_COUNTRY_PHONE = "1"

/**
 * Generates the URL path for a city page based on directory configuration and language settings.
 *
 * The URL format depends on the combination of:
 * - `city.directorySlug`: The directory structure the city belongs to ("city" | "luggage-storage")
 * - `directory`: The intended directory for the URL ("city" | "luggage-storage" | "ads")
 *
 * URL Generation Rules:
 * - If `directory` is "ads": Returns `/a/${slug}` (ads format)
 * - If both `city.directorySlug` and `directory` are "luggage-storage": Returns `/luggage-storage/${slug}`
 * - Returns `/city/${slug}` (default city format)
 *
 * @example
 * // City with luggage-storage directorySlug but requesting city directory (mismatch)
 * getCityUrlPath({
 *   language: "en",
 *   city: { slug: "paris", directorySlug: "luggage-storage" },
 *   directory: "city"
 * })
 * // Returns: "/city/paris" (falls back to default city format)
 */
export const getCityUrlPath = ({
  city: { slug, directorySlug: cityDirectory },
  directory = "luggage-storage",
}: GetCityUrlPathArgs) => {
  if (directory === "ads") {
    return `/a/${slug}`
  }

  if (cityDirectory === "luggage-storage" && directory === "luggage-storage") {
    return `/luggage-storage/${slug}`
  }

  return `/city/${slug}`
}

/**
 * Generates the URL path for a POI (Point of Interest) page based on directory configuration and language settings.
 *
 * The URL format depends on the combination of:
 * - `poi.directorySlug`: The directory structure the POI belongs to ("city" | "luggage-storage")
 * - `directory`: The intended directory for the URL ("city" | "luggage-storage")
 *
 * URL Generation Rules:
 * - If both `poi.directorySlug` and `directory` are "luggage-storage": Returns `/luggage-storage/${citySlug}/${poiSlug}`
 * - Falls back to default format if translations are missing
 *
 * @example
 * // POI with luggage-storage directorySlug but requesting city directory (mismatch)
 * getPoiUrlPath({
 *   language: "en",
 *   city: { slug: "paris" },
 *   poi: { slug: "eiffel-tower", directorySlug: "luggage-storage" },
 *   directory: "city"
 * })
 * // Returns: "/city/paris/eiffel-tower" (falls back to default city format)
 */
export const getPoiUrlPath = ({
  city: { slug: citySlug },
  poi: { slug: poiSlug, directorySlug: poiDirectory },
  directory = "luggage-storage",
}: GetPoiUrlPathArgs) => {
  if (directory === "ads") {
    return `/a/${citySlug}/${poiSlug}`
  }

  if (poiDirectory === "luggage-storage" && directory === "luggage-storage") {
    return `/luggage-storage/${citySlug}/${poiSlug}`
  }

  return `/city/${citySlug}/${poiSlug}`
}

export const getStoreUrlPath = ({ store }: GetStoreUrlPathArgs) => `/stores/${store.city.slug}/${store.slug}`

export const getCitiesUrlPath = () => "/cities"

type UseGenericUrlOptions = RequireAtLeastOne<
  {
    path?: string
    getPath?: ({ language }: { language: string }) => string
  },
  "path" | "getPath"
>

/**
 * A generic function that receives a path or getPath and is used to consistently generate URLs for page types,
 * disregarding any SSR experiments running.
 *
 * @param {UseGenericUrlOptions} options - The options for generating the URL.
 * @param {string} [options.path] - The static path to use for the URL.
 * @param {Function} [options.getPath] - A function that generates the path based on the language.
 * @returns {Object} An object containing the getUrl function and the absolutePath.
 */
export const useGenericUrl = ({ path, getPath }: UseGenericUrlOptions) => {
  const locale = useLocale()
  const { getUrl: getBaseUrl } = useUrl()

  const getUrl = ({ absolute = true, language = locale.language } = {}) =>
    getBaseUrl({
      path: getPath?.({ language }) ?? path,
      absolute,
      language,
    })

  return {
    getUrl,
    absolutePath: getUrl({ absolute: true }),
  }
}

export type UseCityUrlOptions = Pick<GetCityUrlPathArgs, "city" | "directory">

export const useCityUrl = ({ city, directory }: UseCityUrlOptions) =>
  useGenericUrl({
    getPath: () => getCityUrlPath({ city, directory }),
  })

export type UsePoiUrlOptions = Pick<GetPoiUrlPathArgs, "city" | "poi" | "directory">

export const usePoiUrl = ({ city, poi, directory }: UsePoiUrlOptions) =>
  useGenericUrl({
    getPath: () => getPoiUrlPath({ city, poi, directory }),
  })

export const useHomeUrl = () => useGenericUrl({ path: "/" })

export const useAffiliateUrl = () => useGenericUrl({ path: "/ls/affiliate" })

export const useStoreUrl = ({ store }: Pick<GetStoreUrlPathArgs, "store">) =>
  useGenericUrl({ path: getStoreUrlPath({ store }) })

export const useUrl = () => getUrlFromRouter(useRouter())

export const getUrlFromRouter = (router: NextRouter) => {
  const getUrl = ({ path = router.asPath, absolute = false, language = router.locale } = {}) => {
    const currentLanguage = router.locale || DEFAULT_LANGUAGE
    const { url } = getCanonicalUrl({ path, language: language || currentLanguage, currentLanguage, absolute })

    return url === "" ? "/" : url
  }

  return {
    getUrl,
    absolutePath: getUrl({ absolute: true }),
  }
}

// Home slug is in format "US1" or "GB44" (country code + country phone code)
// Next.js can't handle multiple locales on the URL, See issue https://github.com/vercel/next.js/issues/52314
export const getHomeSlug = (country: Country | undefined) =>
  (country?.code ?? DEFAULT_COUNTRY) + (country?.phone ?? DEFAULT_COUNTRY_PHONE)

// Home slug is in format "US1" or "GB44" - we only want the country code part
export const getCountryCodeFromParams = (params: ParsedUrlQuery | undefined) => {
  const countryCodePhone = params?.countryCodePhone
  return typeof countryCodePhone === "string" ? countryCodePhone.replace(/\d+$/, "") : DEFAULT_COUNTRY
}
